import { useState, useEffect, useCallback, useMemo } from 'react';
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import { Meal, Symptom, WeeklyInsight, Exercise } from '../lib/types';
import { localStore, dedupeInsights } from '../lib/storage';
import { syncEngine } from '../lib/syncEngine';
import { buildInsightBrief } from '../lib/insightBrief';
import { resolveInsightResponse } from '../lib/insightClient';
import { migrateAndCleanHistoricalMeals } from '../lib/ingredientUtils';
import { applyScoreRepair, SymptomRepair } from '../lib/scoreRepair';

function ensureUniqueMeals(items: Meal[]): Meal[] {
  if (!Array.isArray(items)) return [];
  const seen = new Set<string>();
  return items.map((m, idx) => {
    let id = (m.id || '').trim();
    if (!id || seen.has(id)) {
      id = `m-${Date.now().toString(36)}-${idx}-${Math.random().toString(36).slice(2, 7)}`;
    }
    seen.add(id);
    return { ...m, id };
  });
}

function sanitizeAndCleanMeals(items: Meal[]): { cleaned: Meal[]; hasChanges: boolean } {
  const unique = ensureUniqueMeals(items);
  return migrateAndCleanHistoricalMeals(unique);
}

function ensureUniqueSymptoms(items: Symptom[]): Symptom[] {
  if (!Array.isArray(items)) return [];
  const seen = new Set<string>();
  return items.map((s, idx) => {
    let id = (s.id || '').trim();
    if (!id || seen.has(id)) {
      id = `s-${Date.now().toString(36)}-${idx}-${Math.random().toString(36).slice(2, 7)}`;
    }
    seen.add(id);
    return { ...s, id };
  });
}

function ensureUniqueExercises(items: Exercise[]): Exercise[] {
  if (!Array.isArray(items)) return [];
  const seen = new Set<string>();
  return items.map((e, idx) => {
    let id = (e.id || '').trim();
    if (!id || seen.has(id)) {
      id = `e-${Date.now().toString(36)}-${idx}-${Math.random().toString(36).slice(2, 7)}`;
    }
    seen.add(id);
    return { ...e, id };
  });
}

export function useData(userId?: string | null) {
  const effectiveUserId = userId || 'usr_harry';

  // Instant one-time startup purge of residual mock data from device storage
  syncEngine.purgeMockData(effectiveUserId);

  const [meals, setMeals] = useState<Meal[]>(() => {
    const raw = localStore.getMeals(effectiveUserId);
    const { cleaned, hasChanges } = sanitizeAndCleanMeals(raw);
    if (hasChanges) {
      localStore.saveMeals(cleaned, effectiveUserId);
    }
    return cleaned;
  });
  const [symptoms, setSymptoms] = useState<Symptom[]>(() => ensureUniqueSymptoms(localStore.getSymptoms(effectiveUserId)));
  const [exercises, setExercises] = useState<Exercise[]>(() => ensureUniqueExercises(localStore.getExercises(effectiveUserId)));
  const [insights, setInsights] = useState<WeeklyInsight[]>(() => localStore.getInsights(effectiveUserId));
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isAiRunning, setIsAiRunning] = useState(false);
  const [syncStatus, setSyncStatus] = useState<'cloud' | 'local'>('local');

  // When userId changes, reload state from storage immediately
  useEffect(() => {
    const raw = localStore.getMeals(effectiveUserId);
    const { cleaned, hasChanges } = sanitizeAndCleanMeals(raw);
    if (hasChanges) {
      localStore.saveMeals(cleaned, effectiveUserId);
    }
    setMeals(cleaned);
    setSymptoms(ensureUniqueSymptoms(localStore.getSymptoms(effectiveUserId)));
    setExercises(ensureUniqueExercises(localStore.getExercises(effectiveUserId)));
    setInsights(localStore.getInsights(effectiveUserId));

    // On the phone the safety vault and older insights live in native storage, which loads asynchronously
    // (and moves any copies still in localStorage across on first run).
    let cancelled = false;
    syncEngine.whenVaultsReady(effectiveUserId);
    localStore.loadInsights(effectiveUserId).then((all) => {
      if (!cancelled && all.length > 0) setInsights((prev) => dedupeInsights([...prev, ...all]));
    });
    return () => {
      cancelled = true;
    };
  }, [effectiveUserId]);

  const authHeaders = useMemo(
    () => ({
      'Content-Type': 'application/json',
      'X-User-Id': effectiveUserId,
    }),
    [effectiveUserId]
  );

  // Background fetch on mount or user switch with 2-way non-destructive merge
  const refreshFromCloud = useCallback(async () => {
    setIsSyncing(true);
    try {
      // The merges below read the safety vault, so make sure it has loaded.
      await syncEngine.whenVaultsReady(effectiveUserId);

      // 1. Flush any pending offline mutations first
      await syncEngine.flush(effectiveUserId);

      // 2. Fetch latest data from cloud endpoints
      const [mealsRes, symptomsRes, exercisesRes, insightsRes] = await Promise.allSettled([
        fetch('/api/meals', { headers: { 'X-User-Id': effectiveUserId } }),
        fetch('/api/symptoms', { headers: { 'X-User-Id': effectiveUserId } }),
        fetch('/api/exercises', { headers: { 'X-User-Id': effectiveUserId } }),
        fetch('/api/insights', { headers: { 'X-User-Id': effectiveUserId } }),
      ]);

      let gotCloudData = false;

      // Current local items
      const currentLocalMeals = localStore.getMeals(effectiveUserId);
      const currentLocalSymptoms = localStore.getSymptoms(effectiveUserId);
      const currentLocalExercises = localStore.getExercises(effectiveUserId);

      // Meals 2-way non-destructive merge
      if (mealsRes.status === 'fulfilled' && mealsRes.value.ok) {
        const cloudMeals: Meal[] = await mealsRes.value.json();
        if (Array.isArray(cloudMeals)) {
          const { merged } = syncEngine.mergeMeals(currentLocalMeals, cloudMeals, effectiveUserId);
          const { cleaned } = sanitizeAndCleanMeals(merged);
          setMeals(cleaned);
          localStore.saveMeals(cleaned, effectiveUserId);
          gotCloudData = true;
        }
      }

      // Symptoms 2-way non-destructive merge
      if (symptomsRes.status === 'fulfilled' && symptomsRes.value.ok) {
        const cloudSymptoms: Symptom[] = await symptomsRes.value.json();
        if (Array.isArray(cloudSymptoms)) {
          const { merged } = syncEngine.mergeSymptoms(currentLocalSymptoms, cloudSymptoms, effectiveUserId);
          const sanitized = ensureUniqueSymptoms(merged);
          setSymptoms(sanitized);
          localStore.saveSymptoms(sanitized, effectiveUserId);
          gotCloudData = true;
        }
      }

      // Exercises 2-way non-destructive merge
      if (exercisesRes.status === 'fulfilled' && exercisesRes.value.ok) {
        const cloudExercises: Exercise[] = await exercisesRes.value.json();
        if (Array.isArray(cloudExercises)) {
          const { merged } = syncEngine.mergeExercises(currentLocalExercises, cloudExercises, effectiveUserId);
          const sanitized = ensureUniqueExercises(merged);
          setExercises(sanitized);
          localStore.saveExercises(sanitized, effectiveUserId);
          gotCloudData = true;
        }
      }

      // Insights
      if (insightsRes.status === 'fulfilled' && insightsRes.value.ok) {
        const cloudInsights = await insightsRes.value.json();
        if (Array.isArray(cloudInsights) && cloudInsights.length > 0) {
          setInsights(cloudInsights);
          localStore.saveInsights(cloudInsights, effectiveUserId);
          gotCloudData = true;
        }
      }

      // 3. Flush any newly discovered local items to cloud
      await syncEngine.flush(effectiveUserId);

      if (gotCloudData) {
        setSyncStatus('cloud');
      }
    } catch (e) {
      console.log('Using offline / local storage state', e);
      setSyncStatus('local');
    } finally {
      setIsSyncing(false);
    }
  }, [effectiveUserId]);

  useEffect(() => {
    refreshFromCloud();

    // Auto-sync when reconnecting to network or bringing app to foreground
    const handleOnline = () => {
      refreshFromCloud();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshFromCloud();
      }
    };

    window.addEventListener('online', handleOnline);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Native iOS app foreground resume listener
    let removeAppListener: (() => void) | undefined;
    if (Capacitor.isNativePlatform()) {
      CapApp.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          refreshFromCloud();
        }
      }).then((handle) => {
        removeAppListener = () => handle.remove();
      }).catch(() => {});
    }

    // Periodic bi-directional cloud sync (flushes local mutations and pulls new items from web app)
    const intervalId = setInterval(() => {
      if (navigator.onLine) {
        refreshFromCloud();
      }
    }, 25000);

    return () => {
      window.removeEventListener('online', handleOnline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(intervalId);
      if (removeAppListener) removeAppListener();
    };
  }, [refreshFromCloud, effectiveUserId]);

  // Undo / Redo history state
  const [undoStack, setUndoStack] = useState<{ meals: Meal[]; symptoms: Symptom[]; exercises: Exercise[] }[]>([]);
  const [redoStack, setRedoStack] = useState<{ meals: Meal[]; symptoms: Symptom[]; exercises: Exercise[] }[]>([]);

  const pushUndoSnapshot = useCallback(() => {
    setUndoStack((prev) => {
      const snapshot = { meals: [...meals], symptoms: [...symptoms], exercises: [...exercises] };
      return [snapshot, ...prev.slice(0, 19)];
    });
    setRedoStack([]);
  }, [meals, symptoms, exercises]);

  const undo = useCallback(() => {
    if (undoStack.length === 0) return;
    const [previous, ...remainingUndo] = undoStack;
    const currentSnapshot = { meals: [...meals], symptoms: [...symptoms], exercises: [...exercises] };

    setRedoStack((prev) => [currentSnapshot, ...prev]);
    setUndoStack(remainingUndo);

    setMeals(previous.meals);
    setSymptoms(previous.symptoms);
    setExercises(previous.exercises || []);
    localStore.saveMeals(previous.meals, effectiveUserId);
    localStore.saveSymptoms(previous.symptoms, effectiveUserId);
    localStore.saveExercises(previous.exercises || [], effectiveUserId);
  }, [undoStack, meals, symptoms, exercises, effectiveUserId]);

  const redo = useCallback(() => {
    if (redoStack.length === 0) return;
    const [next, ...remainingRedo] = redoStack;
    const currentSnapshot = { meals: [...meals], symptoms: [...symptoms], exercises: [...exercises] };

    setUndoStack((prev) => [currentSnapshot, ...prev]);
    setRedoStack(remainingRedo);

    setMeals(next.meals);
    setSymptoms(next.symptoms);
    setExercises(next.exercises || []);
    localStore.saveMeals(next.meals, effectiveUserId);
    localStore.saveSymptoms(next.symptoms, effectiveUserId);
    localStore.saveExercises(next.exercises || [], effectiveUserId);
  }, [redoStack, meals, symptoms, exercises, effectiveUserId]);

  const addMeal = async (newMealData: Omit<Meal, 'id'>) => {
    pushUndoSnapshot();
    const id = 'm-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 7);
    const newMeal: Meal = {
      ...newMealData,
      id,
    };

    setMeals((prev) => {
      const updated = [newMeal, ...prev];
      localStore.saveMeals(updated, effectiveUserId);
      syncEngine.backupToVault('meals', updated, effectiveUserId);
      return updated;
    });

    syncEngine.enqueue({
      entityId: newMeal.id,
      type: 'meal',
      action: 'add',
      data: newMeal,
      userId: effectiveUserId,
    });

    try {
      const res = await fetch('/api/meals', {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(newMeal),
      });
      if (res.ok) {
        syncEngine.dequeue(newMeal.id, effectiveUserId);
        setSyncStatus('cloud');
      } else {
        setSyncStatus('local');
      }
    } catch {
      setSyncStatus('local');
    }

    return newMeal;
  };

  const updateMeal = async (updatedMeal: Meal) => {
    if (!updatedMeal || !updatedMeal.id) return;
    pushUndoSnapshot();
    const cleanId = updatedMeal.id.trim();
    const cleanMeal = { ...updatedMeal, id: cleanId };

    setMeals((prev) => {
      const updated = prev.map((m) => (m.id === cleanId ? cleanMeal : m));
      localStore.saveMeals(updated, effectiveUserId);
      syncEngine.backupToVault('meals', updated, effectiveUserId);
      return updated;
    });

    syncEngine.enqueue({
      entityId: cleanMeal.id,
      type: 'meal',
      action: 'update',
      data: cleanMeal,
      userId: effectiveUserId,
    });

    try {
      const res = await fetch('/api/meals', {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify(cleanMeal),
      });
      if (res.ok) {
        syncEngine.dequeue(cleanMeal.id, effectiveUserId);
        setSyncStatus('cloud');
      } else {
        setSyncStatus('local');
      }
    } catch {
      setSyncStatus('local');
    }
  };

  const deleteMeal = async (id: string) => {
    if (!id || typeof id !== 'string' || !id.trim()) {
      console.warn('deleteMeal called with empty or invalid id:', id);
      return;
    }
    pushUndoSnapshot();
    const cleanId = id.trim();

    // 1. Record permanent tombstone & remove from safety vault so it NEVER resurrects
    syncEngine.addTombstone(cleanId, effectiveUserId);
    syncEngine.deleteFromVault('meals', cleanId, effectiveUserId);

    // 2. Remove from local React state and local storage
    setMeals((prev) => {
      const updated = prev.filter((m) => m.id !== cleanId);
      localStore.saveMeals(updated, effectiveUserId);
      return updated;
    });

    // 3. Queue cloud deletion mutation
    syncEngine.enqueue({
      entityId: cleanId,
      type: 'meal',
      action: 'delete',
      userId: effectiveUserId,
    });

    try {
      const res = await fetch(`/api/meals?id=${encodeURIComponent(cleanId)}`, {
        method: 'DELETE',
        headers: { 'X-User-Id': effectiveUserId },
      });
      if (res.ok) {
        syncEngine.dequeue(cleanId, effectiveUserId);
        setSyncStatus('cloud');
      } else {
        setSyncStatus('local');
      }
    } catch {
      setSyncStatus('local');
    }
  };

  const addSymptom = async (newSymptomData: Omit<Symptom, 'id'>) => {
    pushUndoSnapshot();
    const id = 's-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 7);
    const newSymptom: Symptom = {
      ...newSymptomData,
      id,
    };

    setSymptoms((prev) => {
      const updated = [newSymptom, ...prev];
      localStore.saveSymptoms(updated, effectiveUserId);
      syncEngine.backupToVault('symptoms', updated, effectiveUserId);
      return updated;
    });

    syncEngine.enqueue({
      entityId: newSymptom.id,
      type: 'symptom',
      action: 'add',
      data: newSymptom,
      userId: effectiveUserId,
    });

    try {
      const res = await fetch('/api/symptoms', {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(newSymptom),
      });
      if (res.ok) {
        syncEngine.dequeue(newSymptom.id, effectiveUserId);
        setSyncStatus('cloud');
      } else {
        setSyncStatus('local');
      }
    } catch {
      setSyncStatus('local');
    }

    return newSymptom;
  };

  const updateSymptom = async (updatedSymptom: Symptom) => {
    if (!updatedSymptom || !updatedSymptom.id) return;
    pushUndoSnapshot();
    const cleanId = updatedSymptom.id.trim();
    const cleanSymptom = { ...updatedSymptom, id: cleanId };

    setSymptoms((prev) => {
      const updated = prev.map((s) => (s.id === cleanId ? cleanSymptom : s));
      localStore.saveSymptoms(updated, effectiveUserId);
      syncEngine.backupToVault('symptoms', updated, effectiveUserId);
      return updated;
    });

    syncEngine.enqueue({
      entityId: cleanSymptom.id,
      type: 'symptom',
      action: 'update',
      data: cleanSymptom,
      userId: effectiveUserId,
    });

    try {
      const res = await fetch('/api/symptoms', {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify(cleanSymptom),
      });
      if (res.ok) {
        syncEngine.dequeue(cleanSymptom.id, effectiveUserId);
        setSyncStatus('cloud');
      } else {
        setSyncStatus('local');
      }
    } catch {
      setSyncStatus('local');
    }
  };

  /**
   * Restores user-entered symptom scores (see lib/scoreRepair) on this device and in the cloud.
   * Refuses to run without the pre-repair safety backup. Cloud writes go through the sync queue
   * one at a time (the server rewrites the whole list per request), and stay queued if offline.
   */
  const applySymptomRepairs = async (
    repairs: SymptomRepair[]
  ): Promise<{ repairedCount: number; cloudPendingCount: number }> => {
    if (!(await localStore.getSymptomRepairBackup(effectiveUserId))) {
      throw new Error('No safety backup found; repair not applied.');
    }
    const byId = new Map(repairs.map((r) => [r.id, r]));
    const repaired: Symptom[] = [];
    const updated = symptoms.map((s) => {
      const repair = byId.get(s.id);
      if (!repair) return s;
      const fixed = applyScoreRepair(s, repair);
      repaired.push(fixed);
      return fixed;
    });

    setSymptoms(updated);
    localStore.saveSymptoms(updated, effectiveUserId);
    syncEngine.backupToVault('symptoms', updated, effectiveUserId);

    const pendingAdds = new Set(
      syncEngine
        .getQueue(effectiveUserId)
        .filter((q) => q.type === 'symptom' && q.action === 'add')
        .map((q) => q.entityId)
    );
    for (const fixed of repaired) {
      syncEngine.enqueue({
        entityId: fixed.id,
        type: 'symptom',
        // A check-in that never reached the cloud must still be added, not updated.
        action: pendingAdds.has(fixed.id) ? 'add' : 'update',
        data: fixed,
        userId: effectiveUserId,
      });
    }

    try {
      await syncEngine.flush(effectiveUserId);
    } catch {
      // stays queued; flushed on next sync
    }
    const repairedIds = new Set(repaired.map((r) => r.id));
    const cloudPendingCount = syncEngine
      .getQueue(effectiveUserId)
      .filter((q) => q.type === 'symptom' && repairedIds.has(q.entityId)).length;
    setSyncStatus(cloudPendingCount === 0 ? 'cloud' : 'local');

    return { repairedCount: repaired.length, cloudPendingCount };
  };

  const deleteSymptom = async (id: string) => {
    if (!id || typeof id !== 'string' || !id.trim()) {
      console.warn('deleteSymptom called with empty or invalid id:', id);
      return;
    }
    pushUndoSnapshot();
    const cleanId = id.trim();

    // 1. Record permanent tombstone & remove from safety vault so it NEVER resurrects
    syncEngine.addTombstone(cleanId, effectiveUserId);
    syncEngine.deleteFromVault('symptoms', cleanId, effectiveUserId);

    // 2. Remove from local React state and local storage
    setSymptoms((prev) => {
      const updated = prev.filter((s) => s.id !== cleanId);
      localStore.saveSymptoms(updated, effectiveUserId);
      return updated;
    });

    // 3. Queue cloud deletion mutation
    syncEngine.enqueue({
      entityId: cleanId,
      type: 'symptom',
      action: 'delete',
      userId: effectiveUserId,
    });

    try {
      const res = await fetch(`/api/symptoms?id=${encodeURIComponent(cleanId)}`, {
        method: 'DELETE',
        headers: { 'X-User-Id': effectiveUserId },
      });
      if (res.ok) {
        syncEngine.dequeue(cleanId, effectiveUserId);
        setSyncStatus('cloud');
      } else {
        setSyncStatus('local');
      }
    } catch {
      setSyncStatus('local');
    }
  };

  const addExercise = async (newExerciseData: Omit<Exercise, 'id'>) => {
    pushUndoSnapshot();
    const id = 'e-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 7);
    const newExercise: Exercise = {
      ...newExerciseData,
      id,
    };

    setExercises((prev) => {
      const updated = [newExercise, ...prev];
      localStore.saveExercises(updated, effectiveUserId);
      syncEngine.backupToVault('exercises', updated, effectiveUserId);
      return updated;
    });

    syncEngine.enqueue({
      entityId: newExercise.id,
      type: 'exercise',
      action: 'add',
      data: newExercise,
      userId: effectiveUserId,
    });

    try {
      const res = await fetch('/api/exercises', {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(newExercise),
      });
      if (res.ok) {
        syncEngine.dequeue(newExercise.id, effectiveUserId);
        setSyncStatus('cloud');
      } else {
        setSyncStatus('local');
      }
    } catch {
      setSyncStatus('local');
    }

    return newExercise;
  };

  const updateExercise = async (updatedExercise: Exercise) => {
    if (!updatedExercise || !updatedExercise.id) return;
    pushUndoSnapshot();
    const cleanId = updatedExercise.id.trim();
    const cleanExercise = { ...updatedExercise, id: cleanId };

    setExercises((prev) => {
      const updated = prev.map((e) => (e.id === cleanId ? cleanExercise : e));
      localStore.saveExercises(updated, effectiveUserId);
      syncEngine.backupToVault('exercises', updated, effectiveUserId);
      return updated;
    });

    syncEngine.enqueue({
      entityId: cleanExercise.id,
      type: 'exercise',
      action: 'update',
      data: cleanExercise,
      userId: effectiveUserId,
    });

    try {
      const res = await fetch('/api/exercises', {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify(cleanExercise),
      });
      if (res.ok) {
        syncEngine.dequeue(cleanExercise.id, effectiveUserId);
        setSyncStatus('cloud');
      } else {
        setSyncStatus('local');
      }
    } catch {
      setSyncStatus('local');
    }
  };

  const deleteExercise = async (id: string) => {
    if (!id || typeof id !== 'string' || !id.trim()) {
      console.warn('deleteExercise called with empty or invalid id:', id);
      return;
    }
    pushUndoSnapshot();
    const cleanId = id.trim();

    // 1. Record permanent tombstone & remove from safety vault so it NEVER resurrects
    syncEngine.addTombstone(cleanId, effectiveUserId);
    syncEngine.deleteFromVault('exercises', cleanId, effectiveUserId);

    // 2. Remove from local React state and local storage
    setExercises((prev) => {
      const updated = prev.filter((e) => e.id !== cleanId);
      localStore.saveExercises(updated, effectiveUserId);
      return updated;
    });

    // 3. Queue cloud deletion mutation
    syncEngine.enqueue({
      entityId: cleanId,
      type: 'exercise',
      action: 'delete',
      userId: effectiveUserId,
    });

    try {
      const res = await fetch(`/api/exercises?id=${encodeURIComponent(cleanId)}`, {
        method: 'DELETE',
        headers: { 'X-User-Id': effectiveUserId },
      });
      if (res.ok) {
        syncEngine.dequeue(cleanId, effectiveUserId);
        setSyncStatus('cloud');
      } else {
        setSyncStatus('local');
      }
    } catch {
      setSyncStatus('local');
    }
  };

  const restoreRecoverableData = useCallback(async () => {
    await syncEngine.whenVaultsReady(effectiveUserId);
    const vaultMeals = syncEngine.getAllVaultItems<Meal>('meals', effectiveUserId);
    const vaultSymptoms = syncEngine.getAllVaultItems<Symptom>('symptoms', effectiveUserId);
    const vaultExercises = syncEngine.getAllVaultItems<Exercise>('exercises', effectiveUserId);

    let restoredCount = 0;
    if (vaultMeals.length > 0) {
      const current = localStore.getMeals(effectiveUserId);
      const { merged, newlyDiscoveredCount } = syncEngine.mergeMeals(current, vaultMeals, effectiveUserId);
      const { cleaned } = sanitizeAndCleanMeals(merged);
      setMeals(cleaned);
      localStore.saveMeals(cleaned, effectiveUserId);
      restoredCount += newlyDiscoveredCount;
    }
    if (vaultSymptoms.length > 0) {
      const current = localStore.getSymptoms(effectiveUserId);
      const { merged, newlyDiscoveredCount } = syncEngine.mergeSymptoms(current, vaultSymptoms, effectiveUserId);
      setSymptoms(ensureUniqueSymptoms(merged));
      localStore.saveSymptoms(merged, effectiveUserId);
      restoredCount += newlyDiscoveredCount;
    }
    if (vaultExercises.length > 0) {
      const current = localStore.getExercises(effectiveUserId);
      const { merged, newlyDiscoveredCount } = syncEngine.mergeExercises(current, vaultExercises, effectiveUserId);
      setExercises(ensureUniqueExercises(merged));
      localStore.saveExercises(merged, effectiveUserId);
      restoredCount += newlyDiscoveredCount;
    }

    syncEngine.flush(effectiveUserId).catch(() => {});
    return restoredCount;
  }, [effectiveUserId]);

  const runAiReanalysis = async (): Promise<WeeklyInsight> => {
    setIsAiRunning(true);
    try {
      let brief;
      try {
        brief = buildInsightBrief(meals, symptoms, exercises, { nowMs: Date.now(), previous: insights[0] });
      } catch (e) {
        console.warn('Insight brief build failed (corrupt local data?)', e);
        if (insights[0]) return insights[0];
        throw e;
      }
      const geminiKey = localStore.getGeminiApiKey();
      const geminiModel = localStore.getGeminiModel();
      let status = 0;
      let body: unknown = null;
      try {
        const res = await fetch('/api/insights?reanalyze=true', {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({ brief, geminiApiKey: geminiKey, geminiModel: geminiModel }),
        });
        status = res.status;
        body = await res.json();
      } catch (e) {
        console.log('Insight request failed, using local brief-only insight...', e);
      }
      const insight = resolveInsightResponse(status, body, brief, meals, symptoms);
      const fromServer = status >= 200 && status < 300 && (body as any)?.format_version === 2;
      const updated = [insight, ...insights];
      setInsights(updated);
      localStore.saveInsights(updated, effectiveUserId);
      if (insight.action_plans && Array.isArray(insight.action_plans)) {
        localStore.saveActionPlans(insight.action_plans, effectiveUserId);
      }
      localStore.setLastAiRun(Date.now());
      if (fromServer) setSyncStatus('cloud');
      return insight;
    } finally {
      setIsAiRunning(false);
    }
  };

  const clearAllData = async () => {
    localStore.clearAll(effectiveUserId);
    setMeals([]);
    setSymptoms([]);
    setExercises([]);
    setInsights([]);

    try {
      await fetch('/api/clear', {
        method: 'POST',
        headers: authHeaders,
      });
      setSyncStatus('cloud');
    } catch {
      setSyncStatus('local');
    }
  };

  const loadSampleData = () => {
    localStore.loadSampleData();
    const sMeals = ensureUniqueMeals(localStore.getMeals(effectiveUserId));
    const sSymptoms = ensureUniqueSymptoms(localStore.getSymptoms(effectiveUserId));
    const sExercises = ensureUniqueExercises(localStore.getExercises(effectiveUserId));
    const sInsights = localStore.getInsights(effectiveUserId);
    setMeals(sMeals);
    setSymptoms(sSymptoms);
    setExercises(sExercises);
    setInsights(sInsights);
  };

  const [pendingSyncCount, setPendingSyncCount] = useState<number>(() => syncEngine.getQueue(effectiveUserId).length);

  useEffect(() => {
    const updateCount = () => {
      setPendingSyncCount(syncEngine.getQueue(effectiveUserId).length);
    };
    const id = setInterval(updateCount, 2500);
    return () => clearInterval(id);
  }, [effectiveUserId]);

  return {
    meals,
    symptoms,
    exercises,
    insights,
    isLoading,
    isSyncing,
    isAiRunning,
    syncStatus,
    pendingSyncCount,
    addMeal,
    updateMeal,
    deleteMeal,
    addSymptom,
    updateSymptom,
    deleteSymptom,
    addExercise,
    updateExercise,
    deleteExercise,
    runAiReanalysis,
    applySymptomRepairs,
    refreshFromCloud,
    restoreRecoverableData,
    clearAllData,
    loadSampleData,
    canUndo: undoStack.length > 0,
    canRedo: redoStack.length > 0,
    undo,
    redo,
  };
}
